import React from "react";
import { Star, Quote, ExternalLink, MapPin } from "lucide-react";
import Section from "../ui/Section";

// Google Maps URL for Glow Lawns
const GOOGLE_MAPS_URL = "https://www.google.com/maps/search/glowLAWNS+Resorts+%26+Home-stays+Kavilumpara";

// Sample reviews - Replace with actual customer reviews
const reviews = [
  {
    id: 1,
    name: "Rahul Menon",
    rating: 5,
    date: "2 weeks ago",
    text: "Amazing experience! The tree house was absolutely stunning with breathtaking valley views. The staff was incredibly hospitable and the Kerala breakfast was delicious. Will definitely come back!",
    avatar: "RM"
  },
  {
    id: 2,
    name: "Priya Sharma",
    rating: 5,
    date: "1 month ago",
    text: "Perfect getaway from the city. The location is serene, surrounded by nature. We loved the stone house with private pool. Highly recommend for couples and families looking for a peaceful retreat.",
    avatar: "PS"
  },
  {
    id: 3,
    name: "Arun Kumar",
    rating: 5,
    date: "3 weeks ago",
    text: "One of the best homestays in Kerala! The waterfall views from the tree house are unreal. The hosts made us feel like family. Fresh homemade food and impeccable cleanliness.",
    avatar: "AK"
  },
  {
    id: 4,
    name: "Sneha Nair",
    rating: 5,
    date: "1 month ago",
    text: "Exceeded all expectations! The property is well-maintained and the attention to detail is impressive. Waking up to the sound of birds and misty mountains was magical. A must-visit!",
    avatar: "SN"
  }
];

// Star rating component
const StarRating = ({ rating, size = "sm" }) => {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6"
  };
  
  return (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star
          key={`rating-star-${i}`}
          className={`${sizeClasses[size]} ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-white/20'}`}
        />
      ))}
    </div>
  );
};

// Individual review card
const ReviewCard = ({ review }) => (
  <div className="group p-6 rounded-2xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent backdrop-blur-sm hover:border-[#C9A227]/30 transition-all duration-300">
    {/* Quote icon */}
    <Quote className="w-8 h-8 text-[#C9A227]/30 mb-4" />
    
    {/* Review text */}
    <p className="text-white/70 text-sm leading-relaxed mb-6 line-clamp-4">
      "{review.text}"
    </p>
    
    {/* Reviewer info */}
    <div className="flex items-center gap-3">
      {/* Avatar */}
      <div className="w-10 h-10 rounded-full bg-[#C9A227]/20 border border-[#C9A227]/30 flex items-center justify-center">
        <span className="text-sm font-medium text-[#C9A227]">{review.avatar}</span>
      </div>
      
      <div className="flex-1">
        <h4 className="text-white font-medium text-sm">{review.name}</h4>
        <div className="flex items-center gap-2 mt-0.5">
          <StarRating rating={review.rating} size="sm" />
          <span className="text-white/40 text-xs">{review.date}</span>
        </div>
      </div>
    </div>
  </div>
);

const GoogleReviews = () => {
  // Calculate average rating
  const avgRating = (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1);
  
  return (
    <Section className="py-20 md:py-28 border-t border-white/10" data-testid="google-reviews-section">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          {/* Google Badge */}
          <a 
            href={GOOGLE_MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-5 py-3 rounded-full border border-white/20 bg-white/5 hover:border-[#C9A227]/50 hover:bg-white/10 transition-all duration-300 mb-8 group"
          >
            {/* Google "G" logo */}
            <svg className="w-6 h-6" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="text-white/80 text-sm font-medium group-hover:text-white transition-colors">Google Reviews</span>
            <ExternalLink className="w-4 h-4 text-white/40 group-hover:text-[#C9A227] transition-colors" />
          </a>
          
          {/* Rating Display */}
          <div className="flex flex-col items-center gap-3 mb-6">
            <div className="flex items-baseline gap-2">
              <span className="text-5xl md:text-6xl font-light text-white">{avgRating}</span>
              <Star className="w-8 h-8 text-yellow-400 fill-yellow-400" />
            </div>
            <StarRating rating={5} size="lg" />
            <p className="text-white/50 text-sm">Based on {reviews.length}+ Google Reviews</p>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-light text-white mb-4">
            What Our <span className="text-[#C9A227]">Guests Say</span>
          </h2>
          
          <p className="text-white/60 text-base max-w-2xl mx-auto">
            Real experiences from travelers who found their peaceful escape at Glow Lawns
          </p>
        </div>
        
        {/* Reviews Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>
        
        {/* CTA to Google */}
        <div className="text-center">
          <a
            href={GOOGLE_MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white/10 border border-white/20 text-white hover:bg-[#C9A227]/20 hover:border-[#C9A227]/50 hover:text-[#C9A227] transition-all duration-300 text-base font-medium"
            data-testid="view-all-reviews"
          >
            <MapPin className="w-5 h-5" />
            See All Reviews on Google
            <ExternalLink className="w-4 h-4" />
          </a>
          
          <p className="mt-4 text-white/40 text-sm">
            Love your stay? <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="text-[#C9A227] hover:underline">Leave us a review</a>
          </p>
        </div>
      </div>
    </Section>
  );
};

export default GoogleReviews;
